import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { UserRoleEnum } from 'src/graphql/schema';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { roleOptions } from '../model';

import RoleTooltip from './RoleTooltip';

type Props = {
  defaultValues?: {
    fullName: string;
    email: string;
    role: UserRoleEnum;
    phone: string;
  };
  onSubmit: (data: IUserForm) => void;
  onCancel: () => void;
  btnText?: string | React.ReactNode;
  disableFields?: string[];
  isOwner?: boolean;
};

export const userFormSchema = z.object({
  fullName: z.string().trim().min(1, 'Full name is required.'),
  email: z.string().trim().min(1, 'Email address is required.').email({ message: 'Invalid email format.' }),
  role: z.nativeEnum(UserRoleEnum, {
    message: 'Role is required',
  }),
  phone: z.string().optional(),
});

export type IUserForm = z.infer<typeof userFormSchema>;

const UserForm = ({ defaultValues, onSubmit, onCancel, btnText, disableFields = [], isOwner = false }: Props) => {
  const form = useForm<IUserForm>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      fullName: defaultValues?.fullName || '',
      email: defaultValues?.email || '',
      role: defaultValues?.role || UserRoleEnum.User,
      phone: defaultValues?.phone || '',
    },
  });
  const options = isOwner ? roleOptions.slice(0, 1) : roleOptions.slice(1);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem className="mb-4">
              <FormLabel>Full name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter team member’s name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          disabled={disableFields.includes('email')}
          render={({ field }) => (
            <FormItem className="mb-4">
              <FormLabel>Email address</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter team member’s email address" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem className="mb-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <FormLabel className="mb-0">Phone number</FormLabel>
                <span className="text-input-placeholder text-sm">Optional</span>
              </div>
              <FormControl>
                <Input {...field} placeholder="Enter team member’s phone number" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2.5 mb-4">
                <FormLabel className="mb-0">Role</FormLabel>
                <RoleTooltip />
              </div>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col gap-2">
                  {options.map(({ label, value, description }) => (
                    <FormItem key={value} className="flex items-start gap-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value={value} />
                      </FormControl>
                      <FormLabel className="space-y-1.5">
                        <span className="block font-medium">{label}</span>
                        <p className="text-xs text-muted-foreground whitespace-pre-wrap">{description}</p>
                      </FormLabel>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-between mt-6">
          <Button variant="outline" onClick={onCancel} type="button" size="sm">
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            size="sm"
          >
            {btnText}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default UserForm;
